<template>
  <v-autocomplete
    v-model="selectedValue"
    :items="formattedItems"
    :loading="loading"
    :search-input.sync="search"
    :filter="customFilter"
    item-value="id_fiscal_regla"
    item-text="displayText"
    label="Seleccionar Regla Fiscal"
    placeholder="Buscar por subtipo, forma de pago, régimen, titular..."
    clearable
    no-data-text="No se encontraron reglas"
    @change="handleChange"
    outlined
    dense
    hide-details
  >
    <!-- Selección (compacta: 1 línea + tooltip de impuestos) -->
    <template v-slot:selection="{ item }">
      <div class="d-flex flex-column" style="width: 100%;">
        <!-- Línea 1: texto principal -->
        <div class="text-truncate" style="width: 100%;">
          <strong>{{ item.nombre_pago_forma }}</strong>
          <span v-if="item.nombre_titular_cuenta"> - {{ item.nombre_titular_cuenta }}</span>
        </div>

        <!-- Línea 2: impuestos como texto a color (compacto) -->
        <div class="mt-1 d-flex flex-wrap align-center" style="gap: 10px;">
          <span v-if="item.iva" class="green--text font-weight-medium">
            IVA: {{ formatTax((ivaFrontera ? 0.5 : 1) * item.iva) }}%
          </span>

          <span v-if="item.isr" class="red--text font-weight-medium">
            ISR: {{ formatTax(item.isr) }}%
          </span>

          <span v-if="item.iva_retencion" class="amber--text font-weight-medium">
            IVA Ret: {{ formatTax((ivaFrontera ? 0.5 : 1) * item.iva_retencion) }}%
          </span>

          <span
            v-if="!item.iva && !item.isr && !item.iva_retencion"
            class="grey--text"
          >
            Sin impuestos
          </span>
        </div>
      </div>
    </template>

    <!-- Item -->
    <template v-slot:item="{ item }">
      <v-list-item-content>
        <v-list-item-title>
          <strong>{{ item.nombre_pago_forma }}</strong>
          <span v-if="item.nombre_titular_cuenta"> - {{ item.nombre_titular_cuenta }}</span>
        </v-list-item-title>

        <v-list-item-subtitle class="caption">
          <div class="d-flex flex-wrap align-center">
            <span v-if="item.nombre_gasto_subtipo">{{ item.nombre_gasto_subtipo }}</span>

            <span v-if="item.nombre_regimen_fiscal" class="ml-2 grey--text">
              • {{ item.nombre_regimen_fiscal }}
            </span>

            <span class="ml-2 grey--text">
              • Monto: {{ formatMoney(item.monto_minimo) }} MXN – {{ formatMoney(item.monto_maximo) }} MXN
            </span>
          </div>

          <!-- IMPUESTOS (chips) -->
          <div class="mt-1">
            <v-chip
              v-if="item.iva"
              class="ma-1"
              color="green"
              small
              text-color="white"
            >
              IVA: {{ formatTax((ivaFrontera ? 0.5 : 1) * item.iva) }}%
            </v-chip>

            <v-chip
              v-if="item.isr"
              class="ma-1"
              color="red"
              small
              text-color="white"
            >
              ISR: {{ formatTax(item.isr) }}%
            </v-chip>

            <v-chip
              v-if="item.iva_retencion"
              class="ma-1"
              color="amber"
              small
              text-color="white"
            >
              IVA Ret: {{ formatTax((ivaFrontera ? 0.5 : 1) * item.iva_retencion) }}%
            </v-chip>

            <span
              v-if="!item.iva && !item.isr && !item.iva_retencion"
              class="grey--text"
            >
              Sin impuestos
            </span>
          </div>
        </v-list-item-subtitle>
      </v-list-item-content>
    </template>
  </v-autocomplete>
</template>

<script>
export default {
  name: 'ReglaFiscalAutocomplete',

  props: {
    items: {
      type: Array,
      required: true,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    value: {
      type: String,
      default: null
    },
    ivaFrontera: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      selectedValue: this.value,
      search: null
    }
  },

  computed: {
    formattedItems() {
      return (this.items || []).map(item => ({
        ...item,
        // Texto para búsquedas rápidas / fallback
        displayText: [
          item.nombre_gasto_subtipo,
          item.nombre_pago_forma,
          item.nombre_regimen_fiscal,
          item.nombre_titular_cuenta,
          item.nombre_proveedor_tipo,
          item.nombre_persona_tipo
        ].filter(Boolean).join(' - ')
      }))
    }
  },

  watch: {
    value(newVal) {
      this.selectedValue = newVal
    }
  },

  methods: {
    handleChange(value) {
      this.$emit('input', value)
      this.$emit('change', value)
    },

    customFilter(item, queryText) {
      if (!queryText) return true

      const normalize = (text) =>
        (text || '')
          .toString()
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')

      const q = normalize(queryText)

      const haystack = [
        item.displayText,
        item.nombre_gasto_tipo,
        item.nombre_gasto_subtipo,
        item.nombre_pago_forma,
        item.nombre_titular_cuenta,
        item.nombre_proveedor_tipo,
        item.nombre_persona_tipo,
        item.nombre_regimen_fiscal
      ]
        .filter(Boolean)
        .map(normalize)
        .join(' | ')

      return haystack.includes(q)
    },

    formatTax(value) {
      if (value == null || value === '') return '0'
      const n = Number(value)
      if (Number.isNaN(n)) return value
      return (Math.round(n * 10000) / 10000).toString()
    },

    formatMoney(number) {
      if (number == null || number === '') return ''
      const n = Number(number)
      if (Number.isNaN(n)) return number
      return (
        '$' +
        n
          .toFixed(2)
          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      )
    }
  }
}
</script>

<style scoped>
/* opcional */
</style>
